import React, { useEffect, useMemo, useState } from 'react';
import { useInView } from 'react-intersection-observer';

import BundleCard from './BundleCard';
import { ShouldRender } from '../../shared/basic/ShouldRender';
import Heading from '../../shared/Heading';
import CustomLoader from '../../shared/Loading';
import { notify } from '../../shared/notify';
import { getAllProducts } from '../../../services/Products';
import { PaginationProps } from '../../../utils/interfaces/Orders';
import { BundleData } from '../../../utils/interfaces/Products/bundles';

interface Props {
  heading?: string;
  isPagination?: boolean;
}

const BundlesSection: React.FC<Props> = ({ isPagination = false }) => {
  const [bundleData, setBundleData] = useState<BundleData[]>([]);
  const [loading, setLoading] = useState(false);
  const [paginationData, setPaginationData] = useState<PaginationProps>();
  const [page, setPage] = useState<number>(1);

  const { ref: inViewRef, inView } = useInView({
    threshold: 1,
    triggerOnce: false,
  });

  const handleBundleDetails = async () => {
    if (loading) return;

    try {
      setLoading(true);
      const data = await getAllProducts(
        page,
        7,
        undefined,
        undefined,
        page === 1 ? 6 : 3,
      );
      const formattedData = formatBundleData(data?.data);

      setPaginationData({
        current_page: data?.meta?.current_page,
        per_page: data?.meta?.per_page,
        total: data?.meta?.total,
      });

      setBundleData((prevData) => {
        const uniqueData = prevData.filter(
          (prevItem) =>
            !formattedData.some(
              (newItem) => newItem.hardCopyId === prevItem.hardCopyId,
            ),
        );
        return [...uniqueData, ...formattedData];
      });
    } catch (error: any) {
      notify('Failed to load bundles. Please try again.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const formatBundleData = (rawData: any[]): BundleData[] => {
    return rawData.map((bundle) => ({
      image: bundle?.thumbnail_image || '',
      softCopyId: bundle?.digital_pair?.id || '',
      hardCopyId: bundle?.id || '',
      heading: bundle?.name || '',
      slug: bundle?.slug || '',
      softCopyHeading: bundle?.digital_pair?.name || 0,
      desc: bundle?.description || '',
      currency: bundle?.currency_symbol || '',
      actualPriceHardCopy: bundle?.stroked_price || 0,
      actualPriceSoftCopy:
        (bundle?.digital_pair && bundle.digital_pair?.stroked_price) || 0,
      hardCopyPrice: bundle?.main_price || 0,
      softCopyPrice:
        (bundle?.digital_pair && bundle?.digital_pair.main_price) || 0,
      type: bundle?.category || '',
      external_link: bundle?.external_link || '',
      products: bundle?.bundle_products,
      is_available: bundle?.is_available && bundle?.i18n_attributes,
      i18n_attributes: bundle?.i18n_attributes || {},
    }));
  };

  useEffect(() => {
    handleBundleDetails();
  }, [page]);

  const totalPages = useMemo(
    () =>
      Math.ceil((paginationData?.total || 0) / (paginationData?.per_page || 7)),
    [paginationData?.total, paginationData?.per_page],
  );

  const isLastPage = useMemo(() => page >= totalPages, [page, totalPages]);

  useEffect(() => {
    if (
      inView &&
      !loading &&
      paginationData?.current_page !== paginationData?.total
    ) {
      setPage((prevPage) => prevPage + 1);
    }
  }, [inView]);

  return (
    <>
      <ShouldRender check={bundleData.length}>
        <div>
          <Heading heading='Bundles' route='/product/bundles-list/' />
          <div className='grid md:grid-cols-2 xl:grid-cols-3 gap-6 lg:gap-8'>
            {bundleData?.map((bundle) => (
              <div
                onClick={() => {
                  if (window !== undefined && bundle?.external_link) {
                    window.open(bundle.external_link, '_blank');
                  }
                }}
                key={bundle.hardCopyId}
                className='flex'
              >
                <BundleCard data={bundle} />
              </div>
            ))}
          </div>
          <ShouldRender check={isPagination && !isLastPage}>
            <div ref={inViewRef} className='mt-8 flex justify-center'>
              {loading && <CustomLoader />}
            </div>
          </ShouldRender>
        </div>
      </ShouldRender>
      <ShouldRender check={loading && !bundleData.length}>
        <div className='flex justify-center py-20'>
          <CustomLoader />
        </div>
      </ShouldRender>
    </>
  );
};

export default BundlesSection;
